import { useEffect, useRef } from "react";
import { ChatBubble } from "./components/ChatBubble";
import { ChatInput } from "./components/ChatInput";
import { TypingIndicator } from "./components/TypingIndicator";
import { ConversationList } from "./components/ConversationList";
import { useChatWebSocket } from "./hooks/useChatWebSocket";

function loginRedirect(): void {
  // Pattern D SSO: ?redirect_uri is the post-OAuth landing URL. AP
  // validates it against the allowed-domains whitelist, forwards to
  // /auth/discord internally, and bounces back here after callback.
  const target = new URL(import.meta.env.VITE_AUTH_LOGIN_URL);
  target.searchParams.set("redirect_uri", import.meta.env.VITE_REDIRECT_URI);
  window.location.href = target.toString();
}

function logout(): void {
  const target = new URL(import.meta.env.VITE_AUTH_LOGOUT_URL);
  target.searchParams.set("return", import.meta.env.VITE_REDIRECT_URI);
  window.location.href = target.toString();
}

export function App() {
  const wsUrl = import.meta.env.VITE_WS_URL;
  const { state, sessionId, messages, assistantPending, inputLockedUntil, send } = useChatWebSocket(wsUrl);

  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, assistantPending]);

  useEffect(() => {
    if (state === "auth_required") loginRedirect();
  }, [state]);

  const now = Date.now();
  const lockedRemainingMs = inputLockedUntil && inputLockedUntil > now ? inputLockedUntil - now : 0;
  const inputDisabled = state !== "open" || assistantPending || lockedRemainingMs > 0;

  const banner =
    state === "reconnecting"
      ? "reconnexion en cours…"
      : state === "auth_required"
        ? "redirection login…"
        : state === "connecting"
          ? "connexion…"
          : lockedRemainingMs > 0
            ? `rate limit V0, attends ${Math.ceil(lockedRemainingMs / 1000)}s…`
            : null;

  return (
    <div className="cw-app">
      <header className="cw-header">
        <h1 className="cw-header__title">chat · xilrian</h1>
        <div className="cw-header__right">
          {banner && <span className="cw-header__banner">{banner}</span>}
          {state === "open" && (
            <button
              type="button"
              className="ds-button ds-button--ghost cw-header__logout"
              onClick={logout}
            >
              logout
            </button>
          )}
        </div>
      </header>

      <div className="cw-main">
        <ConversationList sessionId={sessionId} />

        <main className="cw-thread" aria-label="conversation">
          <div className="cw-thread__scroll">
            {messages.length === 0 && state === "open" && (
              <p className="cw-thread__empty">pose ta question à V0…</p>
            )}
            {messages.map((m) => (
              <ChatBubble key={m.message_id} message={m} onSuggestion={send} />
            ))}
            {assistantPending && <TypingIndicator />}
            <div ref={endRef} />
          </div>

          <ChatInput disabled={inputDisabled} onSend={send} />
        </main>
      </div>
    </div>
  );
}
